
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CurrencyConverter from "@/components/CurrencyConverter";
import PopularExchangeRates from "@/components/PopularExchangeRates";
import ForexHomeDeliveryModal from "@/components/ForexHomeDeliveryModal";
import Seo from "@/components/Seo";
import {
  buildBreadcrumbJsonLd,
  buildCanonicalUrl,
  buildWebPageJsonLd,
  organizationJsonLd,
  websiteJsonLd,
} from "@/lib/seo";
import { Truck, ShieldCheck, Clock, MapPin, Building2 } from "lucide-react";

export default function LiveRatesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Live Currency Exchange Rates | Travel Wings Global"
        description="Check live forex rates and convert currencies instantly with Travel Wings Global."
        path="/live-rates"
        structuredData={[
          organizationJsonLd,
          websiteJsonLd,
          buildWebPageJsonLd({
            name: "Live Currency Exchange Rates | Travel Wings Global",
            description:
              "Real-time forex rates and currency converter from Travel Wings Global.",
            url: buildCanonicalUrl("/live-rates"),
            type: "WebPage",
          }),
          buildBreadcrumbJsonLd([
            { name: "Home", url: buildCanonicalUrl("/") },
            { name: "Live Rates", url: buildCanonicalUrl("/live-rates") },
          ]),
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Live <span className="text-green-600">Currency Rates</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get real-time currency exchange rates and convert between different currencies instantly
          </p>
        </div>
      </section>

      {/* Currency Converter Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CurrencyConverter />
        </div>
      </section>

      {/* Forex Service Cards */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Home Delivery Card */}
            <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 p-5 md:p-7 text-white shadow-xl flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-wider opacity-90">Home Delivery</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                Foreign Currency to Your Doorstep
              </h3>
              <p className="text-orange-100 text-sm mb-4">
                Use today's live rates to calculate your order and get foreign currency delivered safely across Delhi NCR (Noida, Gurgaon & more).
              </p>
              <div className="flex flex-col gap-2 text-xs text-orange-100 mb-4">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 shrink-0" /> Secure &amp; Insured Delivery</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 shrink-0" /> Fast Processing</span>
                <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 shrink-0" /> Delhi NCR Delivery</span>
              </div>
              <div className="flex flex-col gap-1.5 text-xs text-orange-200 mb-6">
                <span className="bg-white/10 px-3 py-1.5 rounded-full">Orders up to ₹50,000 → ₹250 delivery charge</span>
                <span className="bg-white/10 px-3 py-1.5 rounded-full">Orders above ₹50,000 → ₹150 delivery charge</span>
              </div>
              <div className="mt-auto">
                <ForexHomeDeliveryModal
                  trigger={
                    <button className="w-full sm:w-auto bg-white text-orange-600 hover:bg-orange-50 font-bold px-6 py-3 rounded-xl shadow-md text-sm transition-all duration-200 flex items-center justify-center gap-2">
                      <Truck className="w-4 h-4" />
                      Order Home Delivery
                    </button>
                  }
                />
              </div>
            </div>

            {/* Branch Pickup Card */}
            <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 p-5 md:p-7 text-white shadow-xl flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-wider opacity-90">Branch Pickup</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                Pick Up from Our Branch
              </h3>
              <p className="text-blue-100 text-sm mb-4">
                Order online using today's live rates and collect your foreign currency at the most convenient Travel Wings location.
              </p>
              <div className="flex flex-col gap-2 mb-6 flex-1">
                {[
                  { name: "Sarita Vihar", addr: "105, 1st Floor, Pocket-B Market, Sarita Vihar, New Delhi – 110076" },
                  { name: "Barakhamba Road", addr: "2-P & 2-F, 2nd Floor, DCM Building, 16 Barakhamba Road, New Delhi – 110001" },
                  { name: "Vasant Kunj", addr: "C-28, Shop #5, Masoodpur, Vasant Kunj, New Delhi – 110070" },
                ].map((b) => (
                  <div key={b.name} className="flex items-start gap-2.5 bg-white/10 rounded-xl px-3 py-2.5">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-blue-200" />
                    <div>
                      <p className="font-semibold text-sm">{b.name}</p>
                      <p className="text-xs text-blue-200 mt-0.5 leading-snug">{b.addr}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-auto">
                <ForexHomeDeliveryModal
                  trigger={
                    <button className="w-full sm:w-auto bg-white text-blue-700 hover:bg-blue-50 font-bold px-6 py-3 rounded-xl shadow-md text-sm transition-all duration-200 flex items-center justify-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Schedule Branch Pickup
                    </button>
                  }
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Live Exchange Rates Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Current <span className="text-green-600">Exchange Rates</span>
            </h2>
            <p className="text-lg text-gray-600">
              Live rates updated every 30 seconds for accurate conversions
            </p>
          </div>
          <PopularExchangeRates />
        </div>
      </section>

      <Footer />
    </div>
  );
}